import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight, ChevronDown } from 'lucide-react';
import { demoDomains, demoUrlFor, domainSummaries, type DemoDomain } from '../../data/demoCenter';
import { clinicalCurrentScope, clinicalFutureCaveat, clinicalFutureOptions, domainDemonstrates, separateWorkspacesCopy } from '../../data/noetivra';
import { fadeUp } from '../../utils/motion';
import { DomainIcon } from '../demoCenter/DomainIcon';
import { DemoStatusBadge } from '../demoCenter/DemoStatusBadge';
import { TalentDetails } from './TalentDetails';

/** "What each workspace demonstrates": one entry per domain, with its own anchor. */
export function NoetivraDomainShowcase() {
  return <section aria-labelledby="demonstrates-title" className="bg-white border-b border-line py-20 lg:py-28" data-id="element-1520" data-mp="2ourp2pe2fu3u">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1521" data-mp="3mbypvnh2boou">
        <motion.h2 {...fadeUp} id="demonstrates-title" className="font-heading font-bold text-3xl lg:text-4xl text-ink-dark tracking-tight leading-tight max-w-2xl mb-6" data-id="element-1522" data-mp="3hjykhfi843kv">
          What each workspace demonstrates
        </motion.h2>
        <motion.p {...fadeUp} className="max-w-3xl text-lg text-ink-muted leading-relaxed mb-12" data-id="element-1523" data-mp="3774thi5dvcjk">
          Each domain is configured for its own participants, records and responsibilities. Successful demo
          interactions show how a workflow behaves; they are not evidence that an assessment method works.
        </motion.p>

        <div className="border-b border-line" data-id="element-1524" data-mp="2nhuiuikhvhsb">
          {demoDomains.map(domain => <DomainEntry key={domain.id} domain={domain} data-id="element-1525" data-mp="29i36sndjgbgy" />)}
        </div>
      </div>
    </section>;
}
function DomainEntry({
  domain
}: {
  domain: DemoDomain;
}) {
  const titleId = `${domain.id}-title`;
  return <article id={domain.id} aria-labelledby={titleId} className="scroll-mt-24 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-16 border-t border-line py-10" data-id="element-1526" data-mp="7r8aluff6a9p">
      <div className="lg:col-span-4" data-id="element-1527" data-mp="b8nmfo911l2t">
        <div className="flex items-center gap-3" data-id="element-1528" data-mp="b9p7mljrbny9">
          <DomainIcon id={domain.id} data-id="element-1529" data-mp="69b1j91puo2v" />
          {domain.status === 'scoped' && <DemoStatusBadge status={domain.status} data-id="element-1530" data-mp="6b44ad97ko5l" />}
        </div>
        <h3 id={titleId} className="mt-4 font-heading font-bold text-2xl text-ink-dark leading-snug" data-id="element-1531" data-mp="1apxszn99m9o">
          {domain.title}
        </h3>
        <p className="mt-1 text-sm font-medium text-lavender" data-id="element-1532" data-mp="3silz9219h06s">{domain.solutionLabel}</p>
        <p className="mt-3 text-sm text-ink-muted" data-id="element-1533" data-mp="u0oot0l0ftgz">{domain.workspaces.join(' · ')}</p>
        <div className="mt-5 flex flex-col items-start gap-2" data-id="element-1534" data-mp="p0asgfsru7ys">
          <a href={demoUrlFor(domain.id)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 font-semibold text-navy hover:text-navy-light transition-colors duration-150 whitespace-nowrap" data-id="element-1535" data-mp="k80ymoojcnr8">
            {domain.actionLabel}
            <ArrowUpRight size={16} aria-hidden="true" data-id="element-1536" data-mp="17xlhl7kovl0i" />
            <span className="sr-only" data-id="element-1537" data-mp="1g7aqd1dtww5e">(opens in a new tab)</span>
          </a>
          {domain.relatedRoute && <a href={domain.relatedRoute.href} className="inline-flex items-center gap-1.5 text-sm font-medium text-ink-muted hover:text-navy transition-colors duration-150 whitespace-nowrap" data-id="element-1538" data-mp="1k7umc1mo1tpy">
              {domain.relatedRoute.label} <ArrowRight size={14} aria-hidden="true" data-id="element-1539" data-mp="1u4ftotm5rgwy" />
            </a>}
        </div>
      </div>

      <div className="lg:col-span-8" data-id="element-1540" data-mp="237gvwj7wknvc">
        <p className="text-xl text-ink-dark leading-relaxed" data-id="element-1541" data-mp="236faplx6akzw">{domainSummaries[domain.id]}</p>
        <ul className="mt-5 space-y-2" data-id="element-1542" data-mp="27xxdmfiravni">
          {domainDemonstrates[domain.id].map(item => <li key={item} className="flex gap-3 text-ink-muted leading-relaxed" data-id="element-1543" data-mp="2cw8kqiho2s21">
              <span aria-hidden="true" className="mt-2.5 h-1 w-3 shrink-0 bg-teal" data-id="element-1544" data-mp="2hwwkptnvij8e" />
              {item}
            </li>)}
        </ul>
        {domain.note && <p className="mt-5 border-l-4 border-teal pl-5 text-ink-dark leading-relaxed" data-id="element-1545" data-mp="2mtdbkvmgjbp6">{domain.note}</p>}
        <DomainExtras id={domain.id} data-id="element-1546" data-mp="3jki2i1o430tc" />
      </div>
    </article>;
}
function DomainExtras({
  id
}: {
  id: DemoDomain['id'];
}) {
  if (id === 'government') {
    return <div className="mt-6 rounded-lg bg-surface-bg p-5" data-id="element-1547" data-mp="3tc4o8ry2siac">
        <p id="workspaces-title" className="scroll-mt-24 font-heading font-semibold text-ink-dark" data-id="element-1548" data-mp="3od1if5a9u81b">
          Separate workspaces, connected workflows.
        </p>
        <p className="mt-2 text-ink-muted leading-relaxed" data-id="element-1549" data-mp="30pto1iedsje2">{separateWorkspacesCopy}</p>
        <a href="/government#workspaces" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-navy hover:text-navy-light transition-colors duration-150 whitespace-nowrap" data-id="element-1550" data-mp="2vqqkk2e1ul87">
          More on the Government workspaces <ArrowRight size={14} aria-hidden="true" data-id="element-1551" data-mp="oxoa2d7morvg" />
        </a>
      </div>;
  }
  if (id === 'talent') {
    return <div className="mt-8" data-id="element-1552" data-mp="kwb5e6g9uuq4">
        <TalentDetails data-id="element-1553" data-mp="kz73bus4djek" />
      </div>;
  }
  if (id === 'clinical') {
    return <div className="mt-6 space-y-4" data-id="element-1554" data-mp="sige9sg9xq5q">
        <p className="text-ink-dark leading-relaxed" data-id="element-1555" data-mp="xfzqynxbpuvk">{clinicalCurrentScope}</p>
        <details className="group rounded-lg border border-line" data-id="element-1556" data-mp="ii7ds2f91ikj">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-5 py-4 font-heading font-semibold text-ink-dark hover:bg-surface-bg transition-colors duration-150 rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal" data-id="element-1557" data-mp="ijqxy4410drl">
            Future research and integration options
            <ChevronDown size={18} aria-hidden="true" className="shrink-0 text-ink-muted transition-transform duration-200 group-open:rotate-180" data-id="element-1558" data-mp="11h1s5aaswh06" />
          </summary>
          <div className="space-y-4 px-5 pb-5" data-id="element-1559" data-mp="16ebshkzqad2s">
            {clinicalFutureOptions.map(option => <p key={option} className="text-ink-muted leading-relaxed" data-id="element-1560" data-mp="1u4ecrexeiequ">
                {option}
              </p>)}
            <p className="text-ink-dark leading-relaxed" data-id="element-1561" data-mp="28s63xqwsuye7">{clinicalFutureCaveat}</p>
          </div>
        </details>
      </div>;
  }
  return null;
}